/**
 * useAuth Hook
 * Wrapper around authStore for convenient auth operations
 */

import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/api/auth';
import { getErrorMessage } from '@/types/errors';
import type { LoginRequest, RequestAccessRequest, MagicLinkVerifyRequest } from '@/types/api';

/**
 * Hook for authentication operations
 */
export function useAuth() {
  const navigate = useNavigate();
  const {
    user,
    accessToken,
    expiresAt,
    isAuthenticated,
    isLoading,
    error,
    login: storeLogin,
    logout: storeLogout,
    setLoading,
    setError,
    initializeFromStorage,
  } = useAuthStore();

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    initializeFromStorage();
  }, [initializeFromStorage]);

  /**
   * Login with email and password
   */
  const login = useCallback(
    async (credentials: LoginRequest) => {
      setLoading(true);
      setError(null);

      try {
        const response = await authService.login(credentials);
        storeLogin(response.user, response.accessToken, response.expiresAt);
        return response;
      } catch (err) {
        const message = getErrorMessage(err);
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setError, storeLogin]
  );

  /**
   * Request access (public registration)
   */
  const requestAccess = useCallback(
    async (request: RequestAccessRequest) => {
      setLoading(true);
      setError(null);

      try {
        const response = await authService.requestAccess(request);
        return response;
      } catch (err) {
        const message = getErrorMessage(err);
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setError]
  );

  /**
   * Verify a magic link token
   */
  const verifyMagicLink = useCallback(
    async (request: MagicLinkVerifyRequest) => {
      setLoading(true);
      setError(null);

      try {
        const response = await authService.verifyMagicLink(request);
        storeLogin(response.user, response.accessToken, response.expiresAt);
        return response;
      } catch (err) {
        const message = getErrorMessage(err);
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setError, storeLogin]
  );

  /**
   * Logout and redirect to login page
   */
  const logout = useCallback(async () => {
    setLoading(true);

    try {
      await authService.logout();
    } catch {
      // Ignore logout API errors - we'll clear local state anyway
    } finally {
      storeLogout();
      setLoading(false);
      navigate('/login', { replace: true });
    }
  }, [setLoading, storeLogout, navigate]);

  /**
   * Fetch and update current user data
   */
  const refreshUser = useCallback(async () => {
    if (!accessToken) return null;

    setLoading(true);
    try {
      const currentUser = await authService.getCurrentUser();
      useAuthStore.getState().setUser(currentUser);
      return currentUser;
    } catch (err) {
      // If fetching user fails, the token is likely invalid
      storeLogout();
      throw err;
    } finally {
      setLoading(false);
    }
  }, [accessToken, setLoading, storeLogout]);

  return {
    // State
    user,
    accessToken,
    expiresAt,
    isAuthenticated,
    isLoading,
    error,

    // Actions
    login,
    logout,
    requestAccess,
    verifyMagicLink,
    refreshUser,
    clearError: () => setError(null),
  };
}
